import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const FoodImage = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [uploadedImages, setUploadedImages] = useState({});
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [uploading, setUploading] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchTodaysMenu();
    }
  }, [userInfo, selectedDate]);

  const fetchTodaysMenu = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get day of week from selected date
      const date = new Date(selectedDate);
      const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
      
      console.log('📅 Fetching menu for:', { selectedDate, dayOfWeek });
      
      const response = await fetch(`${API_URL}/menu/daily/${dayOfWeek}`);
      const data = await response.json();
      
      console.log('📋 Menu response:', data);
      
      if (data.success) {
        setMenuItems(data.items || []);
        console.log('✅ Menu items set:', data.items?.length || 0, 'items');
        
        // Fetch uploaded images for each menu item
        if (data.items && data.items.length > 0) {
          console.log('🖼️ Fetching images for these menu items...');
          fetchUploadedImages(data.items);
        } else {
          console.warn('⚠️ No menu items found for this day');
        }
      } else {
        setError(data.message || 'Failed to load menu items');
        console.error('❌ Menu fetch error:', data.message);
      }
    } catch (err) {
      console.error('Error fetching menu:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedImages = async (items) => {
    try {
      console.log('Fetching images for items:', items.map(i => ({ menu_id: i.menu_id, menu_item: i.menu_item })));
      
      const imagePromises = items.map(item =>
        fetch(`${API_URL}/food-images/${item.menu_id}`)
          .then(res => res.json())
          .then(data => {
            console.log(`✅ Got images for menu_id ${item.menu_id}:`, data);
            return { menuId: item.menu_id, images: data.images || [] };
          })
      );
      
      const results = await Promise.all(imagePromises);
      console.log('Image results:', results);
      
      const imagesMap = {};
      results.forEach(result => {
        imagesMap[result.menuId] = result.images;
      });
      
      console.log('Final imagesMap:', imagesMap);
      setUploadedImages(imagesMap);
    } catch (err) {
      console.error('Error fetching uploaded images:', err);
    }
  };

  const handleImageUpload = async (menuId, mealType, menuItem, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    try {
      setUploading(prev => ({ ...prev, [menuId]: true }));

      const formData = new FormData();
      formData.append('image', file);
      formData.append('menu_id', menuId);
      formData.append('meal_type', mealType);
      formData.append('menu_item', menuItem);
      formData.append('uploaded_by', userInfo.userId);
      formData.append('upload_date', selectedDate);

      const response = await fetch(`${API_URL}/food-images/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Refresh uploaded images
        fetchTodaysMenu();
        alert('Image uploaded successfully!');
      } else {
        alert(data.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [menuId]: false }));
    }
  };

  const handleDeleteImage = async (imageId, menuId) => {
    // DELETE FUNCTIONALITY DISABLED
    // Images cannot be deleted once uploaded
    console.log('🔒 Delete is disabled - images cannot be removed');
    alert('Images cannot be deleted. They are permanent records.');
  };

  const openImageModal = (image) => {
    console.log('📸 Opening modal with image:', image);
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const getMealTypeIcon = (mealType) => {
    const icons = {
      breakfast: '🌅',
      lunch: '🍱',
      snacks: '☕',
      dinner: '🌙'
    };
    return icons[mealType] || '🍽️';
  };

  const getMealTypeColor = (mealType) => {
    const colors = {
      breakfast: '#ff9800',
      lunch: '#4caf50',
      snacks: '#2196f3',
      dinner: '#673ab7'
    };
    return colors[mealType] || '#52ab98';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#ffffff',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '6px solid #f3f3f3',
          borderTop: '6px solid #52ab98',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{
          color: '#52ab98',
          fontSize: '18px',
          fontWeight: '600'
        }}>Loading Menu...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#2b6777', marginBottom: '1rem' }}>Error Loading Menu</h2>
          <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
          <button
            onClick={fetchTodaysMenu}
            style={{
              backgroundColor: '#52ab98',
              color: 'white',
              border: 'none',
              padding: '12px 32px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const groupedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.meal_type]) {
      acc[item.meal_type] = [];
    }
    acc[item.meal_type].push(item);
    return acc;
  }, {});

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f4f7fa 0%, #ffffff 100%);
          overflow: hidden;
        }

        /* Sidebar Styles */
        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #2b6777 0%, #1e4d5a 100%);
          color: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          left: 0;
          top: 0;
          z-index: 1000;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 32px 24px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          display: none;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 700;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .user-details-sidebar {
          flex: 1;
          min-width: 0;
        }

        .user-name-sidebar {
          font-size: 18px;
          font-weight: 600;
          color: white;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
        }

        .sidebar-nav {
          flex: 1;
          padding: 24px 16px;
          overflow-y: auto;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 14px 16px;
          margin-bottom: 8px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .menu-item:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }

        .menu-item-active {
          background: rgba(82, 171, 152, 0.2);
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.15);
        }

        .menu-item-active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 24px;
          background: #52ab98;
          border-radius: 0 4px 4px 0;
        }

        .menu-icon {
          font-size: 22px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .menu-label {
          font-size: 15px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.95);
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(82, 171, 152, 0.1) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .logout-section {
          padding: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .logout-btn {
          width: 100%;
          padding: 14px;
          background: rgba(244, 67, 54, 0.1);
          border: 1px solid rgba(244, 67, 54, 0.3);
          color: #ff6b6b;
          border-radius: 12px;
          cursor: pointer;
          font-size: 15px;
          font-weight: 600;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .logout-btn:hover {
          background: rgba(244, 67, 54, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
        }

        .overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .overlay.show {
          display: block;
          opacity: 1;
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 280px;
          height: 100vh;
          overflow: hidden;
        }

        .top-header {
          background: white;
          padding: 20px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          display: none;
          background: #52ab98;
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 20px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .menu-toggle:hover {
          background: #3d8b7a;
          transform: scale(1.05);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
        }

        .header-greeting {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .header-name {
          font-size: 22px;
          font-weight: 700;
          color: #2b6777;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-header {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .content-wrapper {
          flex: 1;
          overflow-y: auto;
          padding: 32px;
        }

        .content-area {
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 20px;
          padding: 40px;
          margin-bottom: 32px;
          color: white;
          box-shadow: 0 8px 24px rgba(43, 103, 119, 0.2);
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .page-subtitle {
          font-size: 16px;
          opacity: 0.9;
          font-weight: 400;
        }

        .date-selector-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 32px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .date-selector-label {
          font-size: 16px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 12px;
          display: block;
        }

        .date-input {
          width: 100%;
          max-width: 300px;
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 500;
          color: #333;
          transition: all 0.3s ease;
        }

        .date-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82, 171, 152, 0.1);
        }

        .meal-section {
          margin-bottom: 32px;
        }

        .meal-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 3px solid #f0f0f0;
        }

        .meal-icon {
          font-size: 32px;
        }

        .meal-title {
          font-size: 24px;
          font-weight: 700;
          color: #2b6777;
          text-transform: capitalize;
        }

        .menu-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 24px;
        }

        .menu-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid transparent;
        }

        .menu-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          border-color: #52ab98;
        }

        .menu-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .menu-item-info {
          flex: 1;
        }

        .menu-item-name {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .menu-item-desc {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
        }

        .veg-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          background: #e8f5e9;
          color: #2e7d32;
        }

        .nonveg-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          background: #ffebee;
          color: #c62828;
        }

        .upload-section {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 2px dashed #e0e0e0;
        }

        .upload-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 12px;
        }

        .upload-button-wrapper {
          position: relative;
          display: inline-block;
        }

        .upload-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .upload-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .upload-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .file-input {
          position: absolute;
          width: 0.1px;
          height: 0.1px;
          opacity: 0;
          overflow: hidden;
          z-index: -1;
        }

        .uploaded-images {
          margin-top: 16px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 12px;
        }

        .image-thumbnail {
          position: relative;
          aspect-ratio: 1;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .image-thumbnail:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .image-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .image-thumbnail:hover .image-overlay {
          opacity: 1;
        }

        .image-time {
          position: absolute;
          bottom: 4px;
          right: 4px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .empty-text {
          font-size: 18px;
          font-weight: 500;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }

        .image-modal {
          background: white;
          border-radius: 20px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e0e0e0;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
        }

        .modal-close {
          background: #f5f5f5;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 20px;
          color: #666;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .modal-close:hover {
          background: #e0e0e0;
          transform: rotate(90deg);
        }

        .modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .modal-image {
          width: 100%;
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .image-details {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e0e0e0;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-weight: 600;
          color: #666;
        }

        .detail-value {
          color: #333;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 24px;
          border-top: 1px solid #e0e0e0;
        }

        .btn-secondary {
          padding: 12px 24px;
          background: #f5f5f5;
          color: #666;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: #e0e0e0;
        }

        .btn-danger {
          padding: 12px 24px;
          background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
        }

        @media (max-width: 1024px) {
          .main-content {
            margin-left: 0;
          }

          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.open {
            transform: translateX(0);
          }

          .menu-toggle {
            display: flex;
          }

          .close-btn {
            display: flex;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 100%;
            max-width: 320px;
          }

          .content-wrapper {
            padding: 20px;
          }

          .page-header {
            padding: 24px;
          }

          .page-title {
            font-size: 24px;
          }

          .menu-cards-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .header-greeting {
            display: none;
          }

          .page-title {
            font-size: 20px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setSidebarOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">
                  {userInfo?.name || 'Mess Manager'}
                </div>
                <div className="user-role-sidebar">Mess Manager Portal</div>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <MenuItem emoji="🏠" label="Dashboard" route="hostel-mess-dashboard" />
            <MenuItem emoji="🍽️" label="Food Management" route="hostel-mess-food" />
            <MenuItem emoji="📊" label="Daily Report" active={true} route="hostel-mess-foodimage" />
          </nav>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        <div
          className={`overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Daily Food Report</div>
                <div className="header-name">{userInfo?.name || 'Mess Manager'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="content-area">
              <div className="page-header">
                <h1 className="page-title">
                  📸 Daily Food Report
                </h1>
                <p className="page-subtitle">
                  Upload images of prepared food for quality checking and daily records
                </p>
              </div>

              <div className="date-selector-card">
                <label className="date-selector-label">Select Date:</label>
                <input
                  type="date"
                  className="date-input"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              {Object.keys(groupedItems).length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🍽️</div>
                  <p className="empty-text">No menu items found for this day</p>
                </div>
              ) : (
                Object.entries(groupedItems).map(([mealType, items]) => (
                  <div key={mealType} className="meal-section">
                    <div className="meal-header">
                      <span className="meal-icon">{getMealTypeIcon(mealType)}</span>
                      <h2 className="meal-title">{mealType}</h2>
                    </div>

                    <div className="menu-cards-grid">
                      {items.map((item) => (
                        <div key={item.menu_id} className="menu-card">
                          <div className="menu-card-header">
                            <div className="menu-item-info">
                              <h3 className="menu-item-name">{item.menu_item}</h3>
                              {item.description && (
                                <p className="menu-item-desc">{item.description}</p>
                              )}
                            </div>
                            <span className={item.is_vegetarian ? 'veg-badge' : 'nonveg-badge'}>
                              {item.is_vegetarian ? '🌱 Veg' : '🍖 Non-Veg'}
                            </span>
                          </div>

                          <div className="upload-section">
                            <label className="upload-label">Upload Food Image:</label>
                            <div className="upload-button-wrapper">
                              <input
                                type="file"
                                id={`file-${item.menu_id}`}
                                className="file-input"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(item.menu_id, item.meal_type, item.menu_item, e)}
                                disabled={uploading[item.menu_id] || (uploadedImages[item.menu_id] && uploadedImages[item.menu_id].length > 0)}
                              />
                              <label htmlFor={`file-${item.menu_id}`}>
                                <button
                                  className="upload-button"
                                  disabled={uploading[item.menu_id] || (uploadedImages[item.menu_id] && uploadedImages[item.menu_id].length > 0)}
                                  onClick={() => {
                                    if (!(uploadedImages[item.menu_id] && uploadedImages[item.menu_id].length > 0)) {
                                      document.getElementById(`file-${item.menu_id}`).click();
                                    }
                                  }}
                                  type="button"
                                >
                                  {uploading[item.menu_id] ? (
                                    <>⏳ Uploading...</>
                                  ) : uploadedImages[item.menu_id] && uploadedImages[item.menu_id].length > 0 ? (
                                    <>✓ Uploaded</>
                                  ) : (
                                    <>📷 Choose Image</>
                                  )}
                                </button>
                              </label>
                            </div>

                            {uploadedImages[item.menu_id] && uploadedImages[item.menu_id].length > 0 && (
                              <div className="uploaded-images">
                                {uploadedImages[item.menu_id].map((img) => (
                                  <div
                                    key={img.image_id}
                                    className="image-thumbnail"
                                    onClick={() => openImageModal(img)}
                                  >
                                    <img 
                                      src={`${API_URL}${img.image_url}`} 
                                      alt={img.image_caption || 'Food image'}
                                      onError={(e) => {
                                        console.warn('Image failed to load:', img.image_url);
                                        e.target.style.opacity = '0.5';
                                        e.target.style.backgroundColor = '#f0f0f0';
                                      }}
                                    />
                                    <div className="image-overlay">👁️</div>
                                    <div className="image-time">
                                      {new Date(img.upload_date).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Food Image Details</h2>
              <button className="modal-close" onClick={() => setShowImageModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <img
                src={`${API_URL}${selectedImage.image_url}`}
                alt={selectedImage.image_caption || 'Food image'}
                className="modal-image"
                onError={(e) => {
                  console.warn('Modal image failed to load:', selectedImage.image_url);
                  e.target.style.opacity = '0.5';
                  e.target.style.backgroundColor = '#f0f0f0';
                }}
              />

              <div className="image-details">
                <div className="detail-row">
                  <span className="detail-label">Menu Item:</span>
                  <span className="detail-value">{selectedImage.menu_item || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Meal Type:</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedImage.meal_type || 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Upload Date:</span>
                  <span className="detail-value">
                    {new Date(selectedImage.upload_date).toLocaleString()}
                  </span>
                </div>
                {selectedImage.image_caption && (
                  <div className="detail-row">
                    <span className="detail-label">Caption:</span>
                    <span className="detail-value">{selectedImage.image_caption}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowImageModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FoodImage;